import { useEffect, useState } from "react";
import { useAuth } from "../../context/AuthContext";
import { useSubscription } from "../../context/SubscriptionContext";
import {
  getUserScheduledReports,
  cancelScheduledReport,
  deleteScheduledReport,
} from "../../firebase/scheduledReports";
import { trackEvent } from "../../utils/analyticsEvents";
import ScheduleReportModal from "./ScheduleReportModal";
import RecipientListsPanel from "./RecipientListsPanel";
import Modal from "../common/Modal";
import Logo from "../common/Logo";

// ============================================================
// SAFE SCHEDULE NORMALIZATION
// ============================================================
//
// This screen must never crash because one Firestore record is
// malformed, incomplete, or from before one-time scheduling existed —
// every raw record coming back from getUserScheduledReports() is passed
// through here before it's ever rendered or has a property accessed on
// it. normalizeSchedule() returns null for anything it can't make sense
// of; callers filter those out rather than rendering them, and nothing
// here ever deletes or writes to Firestore.

// Firestore Timestamps, raw Dates, ISO strings, and numeric epochs can all
// show up here depending on where the write came from (client SDK vs.
// Admin SDK in the worker) — normalize defensively.
function toDate(value) {
  if (!value) return null;
  if (typeof value.toDate === "function") {
    try { return value.toDate(); } catch { return null; }
  }
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function formatDateTime(value) {
  const date = toDate(value);
  if (!date) return "—";
  return date.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

// Turns one raw Firestore record into a safe, predictable shape for
// display. Handles: null/undefined entries, records missing an id,
// records with a missing/invalid sessionId, and documents created under
// the old recurring-schedule model (no `status` field at all).
//
// Legacy documents (pre one-time-scheduling) are treated as one-time
// schedules using their existing `nextRunAt` as the scheduled time —
// they display safely and will be migrated by the backend worker the
// next time it runs, never deleted or silently discarded here.
function normalizeSchedule(raw) {
  if (!raw || typeof raw !== "object" || !raw.id) return null;

  const isLegacy = !raw.status;

  let status = raw.status;
  if (!status) {
    // Old shape: `enabled: false` meant "paused" — closest safe mapping
    // is "cancelled" (it won't be picked up by the worker either way).
    status = raw.enabled === false ? "cancelled" : "scheduled";
  }

  const scheduledAt = raw.scheduledAt ?? raw.nextRunAt ?? null;
  const sentAt = raw.sentAt ?? (status === "sent" ? raw.lastRunAt ?? null : null);
  const lastError = raw.lastError ?? null;

  return {
    id: raw.id,
    sessionId: typeof raw.sessionId === "string" && raw.sessionId ? raw.sessionId : null,
    reportName: raw.reportName || "Untitled report",
    status,
    isLegacy,
    scheduledAt,
    sentAt,
    lastError,
    recipients: Array.isArray(raw.recipients) ? raw.recipients : [],
    recipientListIds: Array.isArray(raw.recipientListIds) ? raw.recipientListIds : [],
    timezone: raw.timezone || null,
    // Kept so the edit modal can still prefill from a legacy document.
    _raw: raw,
  };
}

const STATUS_SECTIONS = [
  { status: "processing", label: "Processing" },
  { status: "scheduled", label: "Scheduled" },
  { status: "sent", label: "Sent" },
  { status: "failed", label: "Failed" },
  { status: "cancelled", label: "Cancelled" },
];

function StatusBadge({ status }) {
  const styles = {
    scheduled: "bg-blue-950/40 text-blue-300 border-blue-900/60",
    processing: "bg-amber-950/40 text-amber-300 border-amber-900/60",
    sent: "bg-emerald-950/40 text-emerald-300 border-emerald-900/60",
    failed: "bg-red-950/50 text-red-300 border-red-800/60",
    cancelled: "bg-gray-800 text-gray-400 border-gray-700",
  };
  const labels = {
    scheduled: "Scheduled",
    processing: "Processing",
    sent: "Sent",
    failed: "Failed",
    cancelled: "Cancelled",
  };

  return (
    <span className={`text-[11px] font-medium px-2 py-0.5 rounded-full border ${styles[status] || styles.scheduled}`}>
      {labels[status] || "Scheduled"}
    </span>
  );
}

export default function ScheduledReportsScreen({ onClose, onOpenSubscription, sessions = [] }) {
  const { user } = useAuth();
  const { capabilities } = useSubscription();

  const [rawSchedules, setRawSchedules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);

  const [editing, setEditing] = useState(null); // schedule being edited, or "new"
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [tab, setTab] = useState("schedules"); // "schedules" | "lists"

  const load = async () => {
    if (!user) return;
    setLoading(true);
    setLoadError(null);
    try {
      const rows = await getUserScheduledReports(user.uid);
      // Defensive even here: getUserScheduledReports should always
      // resolve to an array, but never trust that at the render layer.
      setRawSchedules(Array.isArray(rows) ? rows : []);
    } catch (err) {
      console.error("Failed to load scheduled reports:", err);
      setLoadError("We couldn't load your scheduled reports. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  // The single source of truth every render below uses. Anything that
  // fails to normalize (null, missing id, etc.) is dropped here — once,
  // in one place — rather than guarded against at every access site.
  const schedules = (rawSchedules || [])
    .map(normalizeSchedule)
    .filter(Boolean);

  const cancelSchedule = async (schedule) => {
    setBusyId(schedule.id);
    try {
      await cancelScheduledReport(schedule.id);
      setRawSchedules((prev) => prev.map((s) => (s?.id === schedule.id ? { ...s, status: "cancelled" } : s)));
      trackEvent("scheduled_report_paused", { status: "cancelled" });
    } catch (err) {
      console.error("Failed to cancel schedule:", err);
    } finally {
      setBusyId(null);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleteBusy(true);
    try {
      await deleteScheduledReport(deleteTarget.id);
      setRawSchedules((prev) => prev.filter((s) => s?.id !== deleteTarget.id));
      trackEvent("scheduled_report_deleted");
      setDeleteTarget(null);
    } catch (err) {
      console.error("Failed to delete schedule:", err);
    } finally {
      setDeleteBusy(false);
    }
  };

  // Never assumes `schedule` is non-null, and never assumes `sessionId`
  // resolves to a session that still exists — both are exactly the
  // conditions that previously crashed this screen.
  const sessionForSchedule = (schedule) => {
    if (!schedule?.sessionId || !Array.isArray(sessions)) return null;
    return sessions.find((s) => s?.firebaseId === schedule.sessionId) || null;
  };

  const canCreate = capabilities.canScheduleReports && sessions.length > 0;

  // Group into sections; only sections with at least one item render, so
  // an account with only "scheduled" items doesn't show four empty
  // headers.
  const sectioned = STATUS_SECTIONS
    .map((section) => ({
      ...section,
      items: schedules.filter((s) => s.status === section.status),
    }))
    .filter((section) => section.items.length > 0);

  return (
    <div className="flex-1 flex flex-col min-w-0 bg-gray-950 text-white h-screen overflow-y-auto">
      <div className="max-w-4xl w-full mx-auto px-4 sm:px-6 py-8 sm:py-12">

        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <Logo size={32} />
            <div>
              <h1 className="text-xl sm:text-2xl font-bold text-white">Scheduled Reports</h1>
              <p className="text-xs sm:text-sm text-gray-500 mt-0.5">
                Schedule this report to be sent once, by email.
              </p>
            </div>
          </div>

          <button
            onClick={onClose}
            className="text-sm bg-gray-800 hover:bg-gray-700 border border-gray-700 transition px-4 py-2 rounded-xl font-medium"
          >
            Back
          </button>
        </div>

        {/* TABS */}
        <div className="flex gap-2 mb-6 border-b border-gray-800">
          {[
            { id: "schedules", label: "Schedules" },
            { id: "lists", label: "Recipient Lists" },
          ].map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition ${
                tab === t.id
                  ? "border-blue-500 text-white"
                  : "border-transparent text-gray-500 hover:text-gray-300"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {tab === "lists" ? (
          <RecipientListsPanel />
        ) : (
        <>

        {/* PREMIUM GATE */}
        {!capabilities.canScheduleReports && (
          <div className="mb-6 bg-blue-950/30 border border-blue-900/50 rounded-2xl p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <h2 className="font-semibold text-white">Scheduled reports are a Premium feature</h2>
              <p className="text-sm text-gray-400 mt-1">
                Upgrade to schedule an AI-summarized report, with a PDF attached, to be sent once at a time you choose.
              </p>
            </div>
            <button
              onClick={onOpenSubscription}
              className="shrink-0 bg-blue-600 hover:bg-blue-700 transition px-5 py-2.5 rounded-xl font-semibold text-sm"
            >
              Upgrade to Premium
            </button>
          </div>
        )}

        {/* NEW SCHEDULE */}
        {capabilities.canScheduleReports && (
          <div className="mb-6 flex items-center justify-between gap-3">
            <p className="text-sm text-gray-500">
              {schedules.length} of {capabilities.maxScheduledReports} schedules used
            </p>
            <button
              onClick={() => setEditing("new")}
              disabled={!canCreate || schedules.length >= capabilities.maxScheduledReports}
              className="bg-blue-600 hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed transition px-4 py-2.5 rounded-xl font-semibold text-sm"
            >
              + Schedule Report
            </button>
          </div>
        )}

        {!canCreate && capabilities.canScheduleReports && (
          <p className="text-xs text-gray-500 mb-4">
            Upload or open a dataset first — schedules are created from an existing session.
          </p>
        )}

        {/* LIST */}
        {loading && (
          <div className="py-16 flex flex-col items-center text-gray-500">
            <span className="w-8 h-8 border-4 border-gray-800 border-t-blue-500 rounded-full animate-spin mb-3" />
            Loading schedules...
          </div>
        )}

        {loadError && !loading && (
          <div className="bg-red-950/30 border border-red-800/50 rounded-xl px-4 py-3 text-sm text-red-300">
            {loadError}
          </div>
        )}

        {!loading && !loadError && schedules.length === 0 && (
          <div className="text-center py-16 border border-dashed border-gray-800 rounded-2xl">
            <div className="text-3xl mb-3" aria-hidden="true">📬</div>
            <p className="text-gray-300 font-medium">No scheduled reports yet</p>
            <p className="text-gray-500 text-sm mt-1">
              Schedule this report to be sent once — pick a date, time, and recipients, and InsightPulse
              will generate and email it automatically.
            </p>
          </div>
        )}

        {!loading && !loadError && sectioned.map((section) => (
          <div key={section.status} className="mb-6">
            <h2 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2.5">
              {section.label} ({section.items.length})
            </h2>

            <div className="space-y-3">
              {section.items.map((schedule) => {
                const matchedSession = sessionForSchedule(schedule);
                const recipientCount = schedule.recipients.length + schedule.recipientListIds.length;

                return (
                  <div key={schedule.id} className="bg-gray-900 border border-gray-800 rounded-2xl p-4 sm:p-5">
                    <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h3 className="font-semibold text-white truncate">{schedule.reportName}</h3>
                          <StatusBadge status={schedule.status} />
                          {schedule.isLegacy && schedule.status === "scheduled" && (
                            <span className="text-[10px] px-2 py-0.5 rounded-full bg-gray-800 text-gray-500 border border-gray-700">
                              one-time (migrated)
                            </span>
                          )}
                        </div>

                        <p className="text-xs text-gray-500 mt-1">
                          {matchedSession?.name || "Original dataset unavailable"}
                        </p>

                        <p className="text-xs text-gray-500 mt-1">
                          {schedule.recipients.length} recipient{schedule.recipients.length === 1 ? "" : "s"}
                          {schedule.recipientListIds.length > 0 && (
                            <> + {schedule.recipientListIds.length} list{schedule.recipientListIds.length === 1 ? "" : "s"}</>
                          )}
                          {recipientCount === 0 && "No recipients"}
                          {" · "}Scheduled for: {formatDateTime(schedule.scheduledAt)}
                          {schedule.sentAt && <> · Sent: {formatDateTime(schedule.sentAt)}</>}
                        </p>

                        {schedule.status === "sent" && (
                          <p className="text-xs text-emerald-400/80 mt-1.5">
                            Delivered once — this report will not be sent again.
                          </p>
                        )}

                        {schedule.status === "failed" && (
                          <p className="text-xs text-red-400 mt-1.5">
                            {schedule.lastError || "This report couldn't be sent."}
                          </p>
                        )}
                      </div>

                      <div className="flex items-center gap-2 shrink-0 self-end sm:self-auto">
                        {schedule.status === "scheduled" && (
                          <>
                            <button
                              onClick={() => cancelSchedule(schedule)}
                              disabled={busyId === schedule.id}
                              className="text-xs px-3 py-1.5 rounded-lg bg-gray-800 hover:bg-gray-700 border border-gray-700 transition font-medium disabled:opacity-50"
                            >
                              Cancel
                            </button>
                            <button
                              onClick={() => setEditing(schedule._raw)}
                              className="text-xs px-3 py-1.5 rounded-lg bg-gray-800 hover:bg-gray-700 border border-gray-700 transition font-medium"
                            >
                              Edit
                            </button>
                          </>
                        )}
                        <button
                          onClick={() => setDeleteTarget(schedule)}
                          className="text-xs px-3 py-1.5 rounded-lg bg-red-950/40 hover:bg-red-950/60 border border-red-900/50 text-red-300 transition font-medium"
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}

        </>
        )}

      </div>

      {/* CREATE / EDIT MODAL */}
      <ScheduleReportModal
        open={!!editing}
        onClose={() => setEditing(null)}
        session={
          editing && editing !== "new"
            ? sessionForSchedule(normalizeSchedule(editing)) || sessions[0]
            : sessions[0]
        }
        existing={editing && editing !== "new" ? editing : null}
        onSaved={load}
      />

      {/* DELETE CONFIRMATION */}
      <Modal
        open={!!deleteTarget}
        onClose={() => !deleteBusy && setDeleteTarget(null)}
        dismissible={!deleteBusy}
        title="Delete this schedule?"
        footer={
          <>
            <button
              onClick={() => setDeleteTarget(null)}
              disabled={deleteBusy}
              className="px-4 py-2.5 rounded-xl bg-gray-800 hover:bg-gray-700 border border-gray-700 transition text-sm font-medium disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={confirmDelete}
              disabled={deleteBusy}
              className="px-4 py-2.5 rounded-xl bg-red-600 hover:bg-red-700 transition text-sm font-semibold disabled:opacity-50"
            >
              {deleteBusy ? "Deleting..." : "Delete Permanently"}
            </button>
          </>
        }
      >
        <p>
          <span className="text-white font-medium">{deleteTarget?.reportName}</span> will be removed.
          This can't be undone.
        </p>
      </Modal>
    </div>
  );
}
